import React, { useState, useRef, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import { GoogleGenAI, Modality } from '@google/genai';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const VoiceSettings = ({ user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(user?.ai_voice_mode || 'male');
  const [isPlaying, setIsPlaying] = useState(null);
  const [previewAudioUrls, setPreviewAudioUrls] = useState({});
  const audioRefs = useRef({});

  // Use the EXACT same voices as your sales agent
  const voiceOptions = [
    {
      id: 'female',
      name: 'Achernar',
      gender: 'Female',
      description: 'Warm and professional female voice',
      previewText: 'Hello! I am Achernar, your MeoAI assistant. How can I help you today?',
      accent: 'Nigerian English',
      voiceName: 'Enceladus'
    },
    {
      id: 'male',
      name: 'Enceladus', 
      gender: 'Male',
      description: 'Clear and confident male voice',
      previewText: 'Hello there! I am Enceladus, ready to assist you.',
      accent: 'Nigerian English',
      voiceName: 'Achernar'
    }
  ];

  // Initialize Google GenAI - same as sales agent
  const initializeAI = useCallback(() => {
    if (!process.env.REACT_APP_GEMINI_API_KEY) {
      showToast('API Key not configured for voice preview', 'error');
      return null;
    }
    return new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });
  }, []);

  // Generate voice preview using the EXACT same configuration as sales agent
  const generateVoicePreview = async (voiceId) => {
    if (!process.env.REACT_APP_GEMINI_API_KEY) {
      showToast('API Key not configured for voice preview', 'error');
      return;
    }

    setIsPlaying(voiceId);
    
    try {
      const ai = initializeAI();
      if (!ai) return;

      const voice = voiceOptions.find(v => v.id === voiceId);
      if (!voice) return;

      // Use the EXACT same model and configuration as your sales agent
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash-native-audio-preview-09-2025', // Same model as sales agent
        contents: voice.previewText,
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: voice.voiceName // Exact same voice names
              }
            }
          }
        }
      });

      // Get the audio data from response - same processing as sales agent
      const audioPart = response.candidates[0]?.content?.parts?.find(part => part.inlineData);
      
      if (audioPart?.inlineData) {
        const audioData = audioPart.inlineData.data;
        const mimeType = audioPart.inlineData.mimeType || 'audio/mp3';
        
        // Create blob URL for the audio
        const byteCharacters = atob(audioData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const audioUrl = URL.createObjectURL(blob);
        
        // Store the audio URL for future use
        setPreviewAudioUrls(prev => ({
          ...prev,
          [voiceId]: audioUrl
        }));

        // Play the audio
        const audio = new Audio(audioUrl);
        audioRefs.current[voiceId] = audio;
        
        audio.onended = () => {
          setIsPlaying(null);
          // Clean up the blob URL after use
          setTimeout(() => {
            URL.revokeObjectURL(audioUrl);
            setPreviewAudioUrls(prev => {
              const newUrls = { ...prev };
              delete newUrls[voiceId];
              return newUrls;
            });
          }, 1000);
        };

        audio.onerror = () => {
          setIsPlaying(null);
          showToast('Failed to play voice preview', 'error');
        };

        await audio.play();
      } else {
        throw new Error('No audio data received');
      }

    } catch (error) {
      console.error('Error generating voice preview:', error);
      
      // Fallback: Use Web Speech API if GenAI fails
      if ('speechSynthesis' in window) {
        try {
          const voice = voiceOptions.find(v => v.id === voiceId);
          const utterance = new SpeechSynthesisUtterance(voice.previewText);
          
          // Configure for natural speech
          utterance.rate = 0.9;
          utterance.pitch = voice.gender === 'Female' ? 1.1 : 1.0;
          utterance.volume = 1;

          // Try to find the best available voice
          const voices = speechSynthesis.getVoices();
          const preferredVoice = voices.find(v => 
            voice.gender === 'Female' ? v.name.includes('Female') || v.name.includes('woman') : 
            v.name.includes('Male') || v.name.includes('man')
          );
          
          if (preferredVoice) {
            utterance.voice = preferredVoice;
          }
          
          utterance.onend = () => setIsPlaying(null);
          utterance.onerror = () => {
            setIsPlaying(null);
            showToast('Failed to play voice preview', 'error');
          };
          
          speechSynthesis.speak(utterance);
        } catch (fallbackError) {
          console.error('Fallback speech synthesis failed:', fallbackError);
          setIsPlaying(null);
          showToast('Voice preview unavailable', 'error');
        }
      } else {
        setIsPlaying(null);
        showToast('Voice preview not supported in this browser', 'error');
      }
    }
  };

  const handlePreviewVoice = async (voiceId) => {
    // Stop any currently playing audio
    Object.values(audioRefs.current).forEach(audio => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    // Stop any speech synthesis
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }

    await generateVoicePreview(voiceId);
  };

  const handleVoiceSelect = (voiceId) => {
    setSelectedVoice(voiceId);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Make API call to save voice preference
      const response = await api.put(API_CONFIG.SETTINGS.VOICE_PREFERENCE, {
  
          ai_voice_mode: selectedVoice
      
      });
      const responseData = response.data

      if (responseData.status === "success"  || responseData.success) {
        showToast('Voice preference saved successfully!', 'success');
        // Update user context or refetch user data here
      } else {
        throw new Error('Failed to save preference');
      }
    } catch (error) {
      console.error('Error saving voice preference:', error);
      showToast('Failed to save voice preference', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getVoiceCardStyle = (voiceId) => {
    const baseStyle = "relative bg-white rounded-lg shadow-md p-6 border-2 transition-all duration-300 cursor-pointer hover:shadow-lg";
    const selectedStyle = "border-blue-500 bg-blue-50";
    const unselectedStyle = "border-gray-200 hover:border-gray-300";
    
    return `${baseStyle} ${selectedVoice === voiceId ? selectedStyle : unselectedStyle}`;
  };

  const getGenderIcon = (gender) => {
    if (gender === 'Female') {
      return (
        <div className="flex items-center justify-center w-12 h-12 bg-pink-100 rounded-lg">
          <svg className="w-6 h-6 text-pink-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm9 11v-1a7 7 0 0 0-7-7h-4a7 7 0 0 0-7 7v1h2v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1z"/>
          </svg>
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
          <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm9 11v-1a7 7 0 0 0-7-7h-4a7 7 0 0 0-7 7v1h2v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1z"/>
          </svg>
        </div>
      );
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      // Clean up audio URLs
      Object.values(previewAudioUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
      
      // Stop any playing audio
      Object.values(audioRefs.current).forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause();
        }
      });
      
      // Stop speech synthesis
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
    };
  }, [previewAudioUrls]);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Voice Preferences</h2>
        <p className="text-gray-600 mt-2">Choose your preferred MeoAI voice assistant - these are the exact same voices used in live conversations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {voiceOptions.map((voice) => (
          <div
            key={voice.id}
            className={getVoiceCardStyle(voice.id)}
            onClick={() => handleVoiceSelect(voice.id)}
          >
            {/* Selected Indicator */}
            {selectedVoice === voice.id && (
              <div className="absolute top-4 right-4">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}

            <div className="flex items-start gap-4 mb-4">
              {getGenderIcon(voice.gender)}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {voice.name}
                </h3>
                <p className="text-sm text-gray-500 capitalize">{voice.gender.toLowerCase()} voice</p>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              {voice.description}
            </p>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Accent:</span>
                <span className="font-medium text-gray-700">{voice.accent}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-500">Preview Text:</span>
                <span className="font-medium text-gray-700 text-right max-w-xs">
                  "{voice.previewText}"
                </span>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviewVoice(voice.id);
                }}
                disabled={isPlaying === voice.id}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPlaying === voice.id ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Preview Voice
                  </>
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVoiceSelect(voice.id);
                }}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  selectedVoice === voice.id
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {selectedVoice === voice.id ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Selected
                  </>
                ) : (
                  'Select'
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Current Selection Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-blue-900">Current Selection</h4>
            <p className="text-blue-700 text-sm">
              {voiceOptions.find(v => v.id === selectedVoice)?.name} -{' '}
              {voiceOptions.find(v => v.id === selectedVoice)?.gender} Voice
            </p>
          </div>
          <div className="text-right">
            <p className="text-blue-600 text-sm">Your MeoAI will use this exact voice</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => {
            setSelectedVoice(user?.ai_voice_mode || 'male');
            showToast('Changes reset', 'info');
          }}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading || selectedVoice === (user?.ai_voice_mode || 'male')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            'Save Voice Preference'
          )}
        </button>
      </div>

      {/* Help Text */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> These are the exact same voices used in live MeoAI conversations. 
              The preview uses the same Google GenAI model and voice configuration as your sales agent, 
              ensuring consistent voice quality across all interactions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSettings;